import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { supabase } from '../lib/supabase';
import { AlertCircle, Loader2, Mail, Lock, X, Eye, EyeOff } from 'lucide-react';
import toast from 'react-hot-toast';
import { sanitizeInput, validateEmail } from '../lib/validation';
import { signIn } from '../lib/auth';
import { startGoogleOAuth } from '../lib/google-oauth';
import { isAdminType } from '../lib/adminRoles';
import { saveOwnOnboardingProfile } from '../lib/onboardingProfile';

interface LoginForm {
  email: string;
  password: string;
  confirmPassword?: string;
}

export function LoginModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, control, getValues } = useForm<LoginForm>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const password = useWatch({ control, name: 'password' });

  const finishSignIn = async (userId: string): Promise<boolean> => {
    await saveOwnOnboardingProfile();

    const { data: userData, error: profileError } = await supabase
      .from('users')
      .select('admin_type')
      .eq('id', userId)
      .maybeSingle();

    if (profileError) throw profileError;
    return isAdminType(userData?.admin_type);
  };

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await startGoogleOAuth();
    } catch (oauthError) {
      setError(oauthError instanceof Error ? oauthError.message : 'Google sign-in could not be started');
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    const sanitizedEmail = sanitizeInput(getValues('email')?.toLowerCase().trim() || '');
    if (!validateEmail(sanitizedEmail)) {
      setError('Enter a valid email address before requesting a reset link.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(sanitizedEmail, {
        redirectTo: 'https://dashboard.scalesmall.ai/reset-password',
      });
      if (resetError) throw resetError;
      toast.success('If an account exists for that email, a reset link is on the way.');
    } catch (resetError) {
      console.error('Password reset request failed:', resetError);
      setError('Password reset could not be requested. Try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: LoginForm) => {
    try {
      setIsLoading(true);
      setError(null);

      if (isCreatingAccount) {
        if (data.password !== data.confirmPassword) {
          throw new Error('Passwords do not match');
        }

        const sanitizedEmail = sanitizeInput(data.email.toLowerCase().trim());

        if (!validateEmail(sanitizedEmail)) {
          throw new Error('Invalid email address');
        }

        // First try to sign in to check if user exists
        const result = await signIn(sanitizedEmail, data.password);

        if (result.success) {
          const isAdmin = result.user ? await finishSignIn(result.user.id) : false;
          // User exists and password is correct, switch to login mode
          setIsCreatingAccount(false);
          toast.success('Account already exists. Signed in successfully!');
          onClose();
          if (isAdmin) navigate('/admin');
          return;
        }

        // If rate limited, show error
        if (result.rateLimited) {
          const blockedUntil = result.blockedUntil ? new Date(result.blockedUntil).toLocaleTimeString() : '';
          throw new Error(`Too many failed attempts. Try again after ${blockedUntil}`);
        }

        // If error is not "Invalid login credentials", then it's a different error
        if (result.error && !result.error.includes('Invalid')) {
          throw new Error(result.error);
        }

        // Try to sign up
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: sanitizedEmail,
          password: data.password
        });

        if (signUpError) throw signUpError;

        if (signUpData.session) {
          await saveOwnOnboardingProfile();
        }

        toast.success(
          signUpData.session
            ? 'Account created successfully!'
            : 'Check your email to confirm your account, then sign in.'
        );
        onClose();
      } else {
        // Try to sign in
        const sanitizedEmail = sanitizeInput(data.email.toLowerCase().trim());

        if (!validateEmail(sanitizedEmail)) {
          throw new Error('Invalid email address');
        }

        const result = await signIn(sanitizedEmail, data.password);

        if (!result.success) {
          if (result.rateLimited) {
            const blockedUntil = result.blockedUntil ? new Date(result.blockedUntil).toLocaleTimeString() : '';
            throw new Error(`Too many failed attempts. Try again after ${blockedUntil}`);
          }
          throw new Error(result.error || 'Failed to login');
        }

        const isAdmin = result.user ? await finishSignIn(result.user.id) : false;

        toast.success('Successfully logged in!');
        onClose();

        // Redirect admin users to admin dashboard
        if (isAdmin) {
          navigate('/admin');
        }
      }
    } catch (err) {
      console.error('Authentication error:', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      toast.error('Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="w-full max-w-md transform overflow-hidden rounded-xl bg-scale-navy text-left align-middle shadow-xl transition-all border border-gray-800"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="onboarding-auth-title"
        >
          <div className="relative p-6 md:p-8">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6 md:mb-8">
              <h2 id="onboarding-auth-title" className="text-xl md:text-2xl font-bold text-white mb-2">
                {isCreatingAccount ? 'Create Account' : 'Welcome Back'}
              </h2>
              <p className="text-gray-400">
                {isCreatingAccount 
                  ? 'Create your account to continue' 
                  : 'Sign in to your account to continue'}
              </p>
            </div>

            {error && (
              <div role="alert" className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start">
                <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="w-full flex items-center justify-center gap-3 px-4 py-2 border border-gray-700 rounded-md shadow-sm text-base font-medium text-white bg-scale-darker hover:bg-scale-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-scale-blue transition-colors mb-6"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-scale-navy text-gray-400">Or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4 md:space-y-6">
              <div>
                <label htmlFor="onboarding-auth-email" className="block text-sm font-medium text-white mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="onboarding-auth-email"
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    required
                    autoComplete="email"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'onboarding-auth-email-error' : undefined}
                    className="block w-full pl-10 pr-3 py-2 bg-scale-darker border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-scale-blue focus:border-transparent"
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p id="onboarding-auth-email-error" role="alert" className="mt-1 text-sm text-red-400">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="onboarding-auth-password" className="block text-sm font-medium text-white mb-1">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    id="onboarding-auth-password"
                    type={showPassword ? "text" : "password"}
                    {...register('password', {
                      required: 'Password is required',
                      validate: value => !isCreatingAccount || value.length >= 12 || 'Password must be at least 12 characters'
                    })}
                    required
                    autoComplete={isCreatingAccount ? 'new-password' : 'current-password'}
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={errors.password ? 'onboarding-auth-password-error' : undefined}
                    className="block w-full pl-10 pr-10 py-2 bg-scale-darker border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-scale-blue focus:border-transparent"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p id="onboarding-auth-password-error" role="alert" className="mt-1 text-sm text-red-400">{errors.password.message}</p>
                )}
              </div>

              {isCreatingAccount && (
                <div>
                  <label htmlFor="onboarding-auth-confirm-password" className="block text-sm font-medium text-white mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="onboarding-auth-confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: value => value === password || 'Passwords do not match'
                      })}
                      required
                      autoComplete="new-password"
                      aria-invalid={Boolean(errors.confirmPassword)}
                      aria-describedby={errors.confirmPassword ? 'onboarding-auth-confirm-password-error' : undefined}
                      className="block w-full pl-10 pr-10 py-2 bg-scale-darker border border-gray-700 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-scale-blue focus:border-transparent"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      aria-label={showConfirmPassword ? 'Hide confirmation password' : 'Show confirmation password'}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p id="onboarding-auth-confirm-password-error" role="alert" className="mt-1 text-sm text-red-400">{errors.confirmPassword.message}</p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between gap-4">
                {!isCreatingAccount && (
                  <button
                    type="button"
                    className="text-sm text-scale-blue hover:text-white transition-colors"
                    onClick={handlePasswordReset}
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                )}
                <button
                  type="button"
                  className="ml-auto text-sm text-scale-blue hover:text-white transition-colors"
                  onClick={() => setIsCreatingAccount(!isCreatingAccount)}
                >
                  {isCreatingAccount ? 'Back to Login' : 'Create Account'}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-scale-dark bg-scale-blue hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-scale-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    {isCreatingAccount ? 'Creating Account...' : 'Signing in...'}
                  </>
                ) : (
                  isCreatingAccount ? 'Create Account' : 'Sign in'
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
